import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { Card, Button, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { FaFan } from 'react-icons/fa';
import API_BASE_URL from './config/api';

import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import Header from './Components/Header/BlueHeader';
import Sidebar from './Components/Sidebar/Sidebar';
import Pills from './Components/Pills/PillsV2';
import SearchBar from './Components/SearchBar/SearchBar';
import StatusMultiSelect from './Components/StatusMultiSelect/StatusMultiSelect';
import FancoilTable from './Components/FancoilTable/FancoilTable';
import NotifyResidentModal from './Components/NotifyResidentModal/NotifyResidentModal';
import Alert from 'react-bootstrap/Alert';

// Normalize a Type-ish field value: lowercase, collapse whitespace/dashes/underscores.
const normalizeTypeValue = (raw) => {
  if (typeof raw !== 'string') return '';
  return raw.toLowerCase().replace(/[\s_,-]+/g, ' ').trim();
};

// Recognize a cycle as a "dryer" cycle by inspecting EVERY type-ish field on it.
// Some cycles (especially combined fancoil + dryer cycles) put 'hvac' in `Type`
// and put the actual scope in `maintenanceType` (e.g. "fancoil, dryer vent").
const isDryerCycle = (cycle = {}) => {
  if (!cycle || typeof cycle !== 'object') return false;
  const candidates = [
    cycle.Type,
    cycle.type,
    cycle.inspectionType,
    cycle.inspection_type,
    cycle.contractType,
    cycle.contract_type,
    cycle.serviceType,
    cycle.service_type,
    cycle.maintenanceType,
    cycle.maintenance_type,
    cycle.scope,
  ];
  for (const raw of candidates) {
    const t = normalizeTypeValue(raw);
    if (!t) continue;
    if (t === 'dryer' || t === 'dryer vent' || t === 'dryer vents') return true;
    if (t.includes('dryer') || t.includes('vent')) return true;
  }
  return false;
};

function DryerVents() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [buildingName, setBuildingName] = useState("");
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState("");
  const [maintenanceCycles, setMaintenanceCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [hasDryerVentContract, setHasDryerVentContract] = useState(null);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFormat, setDataFormat] = useState("old"); // Track data format: "old" or "new"

  // Real data from API
  const [stats, setStats] = useState({ totalUnits: 0, noIssues: 0, foundIssues: 0 });
  const [unitsData, setUnitsData] = useState([]);

  // Table state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortColumn, setSortColumn] = useState('unit');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [issueFilters, setIssueFilters] = useState([]);
  // Multi-status filter — defaults to "Needs Service". Can be set to ['Missed']
  // when the manager clicks "See No Access Units" on the new pill.
  const [statusFilter, setStatusFilter] = useState(['Needs Service']);

  // Modal slideshow
  const [showModal, setShowModal] = useState(false);
  const [carouselPhotos, setCarouselPhotos] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Notify resident modal
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [residentEmail, setResidentEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [showInviteSuccess, setShowInviteSuccess] = useState(false);

  // Read the token & buildingId from localStorage
  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || "";
  let buildingId = "";
  
  if (userStr) {
    try {
      const userObj = JSON.parse(userStr);
      buildingId = userObj.buildingId || "";
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }

  // Fetch building data and dryer vent data
  useEffect(() => {
    const fetchData = async () => {
      if (!buildingId || !token) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch building data to check for dryer vent contract and cycles
        const buildingResp = await fetch(
          `${API_BASE_URL}/api/getBuildingById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );

        if (buildingResp.ok) {
          const buildingData = await buildingResp.json();
          setBuildingName(buildingData.name || "Unknown");
          setBuildingManagerName(Object.prototype.hasOwnProperty.call(buildingData, 'manager') ? buildingData.manager || '' : buildingData.managerProfile?.manager || '');
          setBuildingPhotoUrl(buildingData.photo_url || "");
          
          // Check if building has dryer vent contract
          const contracts = buildingData.contracts || [];
          const hasContract = contracts.includes("dryer-vent");
          setHasDryerVentContract(hasContract);
          setHasProjectContract(contracts.includes('project'));
          setHasValveAuditContract(contracts.includes('valve-audit'));

          // Fetch maintenance cycles from MaintenanceInspections table
          const cyclesResp = await fetch(
            `${API_BASE_URL}/api/getMaintenanceInspections`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({ buildingId })
            }
          );

          let dryerVentCycles = [];
          if (cyclesResp.ok) {
            const cyclesData = await cyclesResp.json();
            // Log all cycles fetched for this building
            console.log("All MaintenanceInspections fetched:", cyclesData.maintenanceInspections);
            // Only include cycles for the current building AND Type = "dryer"
            dryerVentCycles = (cyclesData.maintenanceInspections || [])
              .filter(c => String(c.buildingId) === String(buildingId) && isDryerCycle(c));
            console.log("Filtered dryer cycles for building:", dryerVentCycles);
            if (dryerVentCycles.length === 0) {
              console.log("No dryer cycles found for this building.");
            }
            dryerVentCycles = dryerVentCycles.map(cycle => {
              let display = cycle.cycle || cycle.cycleName || cycle.name || "Unnamed Cycle";
              return {
                ...cycle,
                maintenanceID: cycle.maintenanceID || cycle.objectId, // Use actual maintenanceID field if available
                cycle: display
              };
            });
            
            // Sort cycles by createdAt in descending order (most recent first)
            dryerVentCycles.sort((a, b) => {
              const dateA = new Date(a.createdAt || a.created_at || 0);
              const dateB = new Date(b.createdAt || b.created_at || 0);
              return dateB - dateA; // Descending order (newest first)
            });
            
            console.log("Mapped and sorted Dryer Vent Cycles for Dropdown:", dryerVentCycles);
          }
          setMaintenanceCycles(dryerVentCycles);
          const mostRecentCycle = dryerVentCycles[0]; // Most recent cycle is now first
          setSelectedCycle(mostRecentCycle?.cycle || "");
          setSelectedMaintenance(mostRecentCycle || null);

          if (hasContract && mostRecentCycle) {
            // Fetch data for the most recent cycle - pass hasContract directly
            await fetchCycleData(mostRecentCycle, hasContract);
          } else if (hasContract && dryerVentCycles.length === 0) {
            // FALLBACK: building has dryer-vent contract but no MaintenanceInspections
            // record of Type='dryer' (common for older buildings whose cycle predates
            // the Type field). The OLD-format endpoints only need buildingId, so we
            // can still pull their data and stats directly.
            console.log('[DryerVents] No typed-dryer cycle found — falling back to OLD-format direct fetch by buildingId.');
            await fetchOldFormatByBuilding();
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setHasDryerVentContract(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, buildingId]);

  // Fallback: pull OLD-format dryer vent data + stats by buildingId only.
  // Used when the building has a dryer-vent contract but no Type='dryer'
  // MaintenanceInspections record (older buildings). The OLD endpoints don't
  // need a maintenance cycle, just the building id.
  const fetchOldFormatByBuilding = async () => {
    setDataFormat('old');
    try {
      const statsResp = await fetch(
        `${API_BASE_URL}/api/getDryerVentStats`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ buildingId }),
        }
      );
      if (statsResp.ok) {
        const statsData = await statsResp.json();
        setStats(statsData.stats || { totalUnits: 0, noIssues: 0, foundIssues: 0 });
      }

      const dataResp = await fetch(
        `${API_BASE_URL}/api/getDryerVentData`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ buildingId }),
        }
      );
      if (dataResp.ok) {
        const dataResult = await dataResp.json();
        setUnitsData(dataResult.dryerVentData || []);
      }
    } catch (err) {
      console.error('[DryerVents] Error in fetchOldFormatByBuilding:', err);
    }
  };

  // Function to fetch data for a specific cycle
  const fetchCycleData = async (cycle, hasContract = null) => {
    // Use the passed hasContract parameter, or fall back to state
    const contractCheck = hasContract !== null ? hasContract : hasDryerVentContract;
    if (!cycle || !contractCheck) {
      console.log(`[DryerVents] fetchCycleData skipped - cycle: ${!!cycle}, contract: ${contractCheck}`);
      return;
    }
    
    // Use the maintenanceID field from the cycle (e.g., "123-2")
    const maintenanceId = cycle.maintenanceID || cycle.objectId;
    const formatVersion = cycle.dataFormatVersion || "old";
    setDataFormat(formatVersion);
    
    console.log(`[DryerVents] Fetching data for maintenanceId: ${maintenanceId}, formatVersion: ${formatVersion}, cycle:`, cycle);
    
    try {
      if (formatVersion === "new") {
        // New format: use getTableData and getDashboardData with equipmentType="dryer"
        
        // Fetch dashboard data (stats) - pass equipmentType for filtering
        const dashRes = await fetch(
          `${API_BASE_URL}/api/getDashboardData`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ 
              maintenanceId,
              dataFormatVersion: formatVersion,
              equipmentType: "dryer"  // Only show dryer equipment
            })
          }
        );

        if (dashRes.ok) {
          const dashData = await dashRes.json();
          setStats(dashData.stats || { totalUnits: 0, noIssues: 0, foundIssues: 0 });
        }

        // Fetch table data
        const unitsRes = await fetch(
          `${API_BASE_URL}/api/getTableData`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ 
              maintenanceId, 
              limit: 1000, 
              skip: 0,
              dataFormatVersion: formatVersion,
              equipmentType: "dryer"
            })
          }
        );

        if (unitsRes.ok) {
          const unitsData = await unitsRes.json();
          setUnitsData(unitsData.unitsData || []);
        }
      } else {
        // Old format: use getDryerVentStats and getDryerVentData
        const statsResp = await fetch(
          `${API_BASE_URL}/api/getDryerVentStats`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );

        if (statsResp.ok) {
          const statsData = await statsResp.json();
          setStats(statsData.stats);
        }

        const dataResp = await fetch(
          `${API_BASE_URL}/api/getDryerVentData`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );

        if (dataResp.ok) {
          const dataResult = await dataResp.json();
          setUnitsData(dataResult.dryerVentData || []);
        }
      }
    } catch (err) {
      console.error("Error fetching cycle data:", err);
    }
  };

  // Handle cycle change
  const handleCycleChange = async (newCycle) => {
    if (!newCycle || !maintenanceCycles.length) return;
    
    const selectedCycleObj = maintenanceCycles.find(c => c.cycle === newCycle);
    if (!selectedCycleObj) return;
    
    setSelectedCycle(newCycle);
    setSelectedMaintenance(selectedCycleObj);
    // Pass hasDryerVentContract from state (should be set by now)
    await fetchCycleData(selectedCycleObj, hasDryerVentContract);
  };

  const handleNavigate = (key) => {
    switch (key) {
      case 'hvac':
        navigate('/');
        break;
      case 'mech':
        navigate('/mechanical-room');
        break;
      case 'dryerVents':
        navigate('/dryer-vents');
        break;
      case 'stackCleaning':
        navigate('/stack-cleaning');
        break;
      case 'waterSavings':
        navigate('/water-savings');
        break;
      case 'reports':
        navigate('/reports');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'valveAudit':
        navigate('/valve-audit');
        break;
      default:
        break;
    }
  };

  // OLD-format dryer vent data marks no-access via `reason_missed`; NEW format
  // sets `inspection: 'Missed'`. Normalize once so the rest of the page can
  // treat both consistently.
  const normalizedUnits = React.useMemo(() => (
    unitsData.map(row => {
      const hasReasonMissed = row?.reason_missed && String(row.reason_missed).trim() !== '';
      if (row?.inspection !== 'Missed' && hasReasonMissed) {
        return { ...row, inspection: 'Missed' };
      }
      return row;
    })
  ), [unitsData]);

  // Filter data — status filter is an array (multi-select). Pulls from the
  // normalized list so the "Missed" filter shows no-access dryer vent units.
  const filteredData = normalizedUnits.filter((row) => {
    const activeStatuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
    const allStatus = activeStatuses.includes('All Status');
    if (!allStatus) {
      if (!activeStatuses.includes(row.inspection)) return false;
    } else if (row.inspection === 'Missed') {
      // "All Status" hides missed rows by default; users select 'Missed' explicitly
      return false;
    }

    // Search match
    const unitStr = row.unit?.toLowerCase() || '';
    const notesStr = row.notes?.toLowerCase() || '';
    const st = searchTerm.toLowerCase();
    return unitStr.includes(st) || notesStr.includes(st);
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];

    // If sorting by "unit", parse them as numbers
    if (sortColumn === 'unit') {
      valA = parseInt(valA, 10);
      valB = parseInt(valB, 10);
    }
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valA < valB) return (sortDirection === 'asc' ? -1 : 1);
    if (valA > valB) return (sortDirection === 'asc' ? 1 : -1);
    return 0;
  });

  // Paginate
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = sortedData.slice(startIndex, endIndex);

  // Calculate stats for pills — exclude missed units so totals reflect units we
  // actually inspected. Missed count is tracked separately by PillsV2.
  const pillsStats = React.useMemo(() => {
    const inspected = normalizedUnits.filter(row => row.inspection !== 'Missed');
    const totalUnits = inspected.length;
    const noIssues = inspected.filter(row => row.inspection === 'OK' || row.inspection === 'Resolved').length;
    const foundIssues = inspected.filter(row => row.inspection === 'Needs Service').length;
    return {
      totalUnits,
      noIssues,
      foundIssues,
    };
  }, [normalizedUnits]);

  // Auto-switch to "All Status" only when data has loaded AND there are no issues to show
  // (Don't run on initial load when data hasn't arrived yet)
  useEffect(() => {
    const hasNeedsService =
      Array.isArray(statusFilter) && statusFilter.length === 1 && statusFilter[0] === 'Needs Service';
    if (!isLoading && unitsData.length > 0 && pillsStats.foundIssues === 0 && hasNeedsService) {
      setStatusFilter(['All Status']);
    }
  }, [isLoading, unitsData.length, pillsStats.foundIssues, statusFilter]);

  // Handlers
  function handleRowsPerPageChange(e) {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }
  function handlePrevPage() {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  }
  function handleNextPage() {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  }
  function handleSort(columnKey) {
    if (sortColumn === columnKey) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }

  // Slideshow
  function handleOpenCarousel(photos, index) {
    setCarouselPhotos(photos);
    setCarouselIndex(index);
    setShowModal(true);
  }
  function handleCloseModal() {
    setShowModal(false);
  }
  function handleSelectCarousel(selectedIndex) {
    setCarouselIndex(selectedIndex);
  }

  // Helper: determine if a file is a video
  function isVideoUrl(url) {
    const videoExts = [".mp4", ".mov", ".webm", ".ogg", ".m4v"];
    return videoExts.some((ext) => url.toLowerCase().endsWith(ext));
  }

  const onNotifyResident = (row) => {
    // Handle opening notify resident modal
    setSelectedUnit(row);
    setCustomMessage(`Dear Resident of Unit ${row.unit},

This is a notice from your building manager at ${buildingName}. Hudson Condominium Solutions recently conducted a dryer vent maintenance inspection and identified some issues that should be addressed to avoid potential fire hazards and maintain optimal performance.

You can view the full inspection report and details of the identified issues here: https://hudsonhvac.ca/unit-report.html?unit=${row.unit}&a=${buildingId}&add=${encodeURIComponent(buildingName)}&type=dryer`);
    setShowNotifyModal(true);
  };

  const onUpdateStatus = async (row, newStatus) => {
    try {
      console.log(`[DryerVents] onUpdateStatus called: row.objectId=${row.objectId}, newStatus=${newStatus}, dataFormat=${dataFormat}`);
      
      // For new format, use the unified updateUnitStatus endpoint
      // For old format, use the old updateDryerVentStatus endpoint
      if (dataFormat === "new") {
        const payload = {
          objectId: row.objectId,
          newStatus: newStatus,
          dataFormat: "new" // Tell server to use insuiteMaintenanceData table
        };

        console.log(`[DryerVents] Calling updateUnitStatus with payload:`, payload);

        const response = await fetch(
          `${API_BASE_URL}/api/updateUnitStatus`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          }
        );

        console.log(`[DryerVents] updateUnitStatus response status:`, response.status);

        if (response.ok) {
          // Refresh data from server to ensure consistency
          if (selectedMaintenance && (selectedMaintenance.maintenanceID || selectedMaintenance.maintenanceId)) {
            const maintenanceId = selectedMaintenance.maintenanceID || selectedMaintenance.maintenanceId;
            console.log(`[DryerVents] Refreshing data for maintenanceId:`, maintenanceId);
            
            // Re-fetch dashboard and table data
            const dashboardResp = await fetch(
              `${API_BASE_URL}/api/getDashboardData`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                  maintenanceId: maintenanceId,
                  dataFormatVersion: "new",
                  equipmentType: "dryer"
                })
              }
            );
            
            const tableResp = await fetch(
              `${API_BASE_URL}/api/getTableData`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                  maintenanceId: maintenanceId,
                  dataFormatVersion: "new",
                  equipmentType: "dryer",
                  limit: 1000,
                  skip: 0
                })
              }
            );
            
            if (dashboardResp.ok && tableResp.ok) {
              const dashboardData = await dashboardResp.json();
              const tableData = await tableResp.json();
              
              console.log(`[DryerVents] Refreshed stats:`, dashboardData.stats);
              console.log(`[DryerVents] Refreshed unitsData count:`, tableData.unitsData?.length || 0);
              
              setStats(dashboardData.stats || { totalUnits: 0, noIssues: 0, foundIssues: 0 });
              setUnitsData(tableData.unitsData || []);
            } else {
              console.error(`[DryerVents] Failed to refresh data: dashboardResp.ok=${dashboardResp.ok}, tableResp.ok=${tableResp.ok}`);
            }
          } else {
            console.error(`[DryerVents] No selectedMaintenance found for refresh`);
          }
        } else {
          const errorText = await response.text();
          console.error(`[DryerVents] Failed to update status: ${response.status} - ${errorText}`);
        }
      } else {
        // Old format: use the old endpoint
        let recommendation = false;
        if (newStatus === 'Needs Service') {
          recommendation = true;
        } else if (newStatus === 'OK' || newStatus === 'Resolved') {
          recommendation = false;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/updateDryerVentStatus`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              objectId: row.objectId,
              recommendation: recommendation
            })
          }
        );

        if (response.ok) {
          // Update the local state
          setUnitsData(prev => 
            prev.map(item => 
              item.objectId === row.objectId ? { ...item, inspection: newStatus } : item
            )
          );

          // Refresh stats
          const statsResp = await fetch(
            `${API_BASE_URL}/api/getDryerVentStats`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({ buildingId })
            }
          );

          if (statsResp.ok) {
            const statsData = await statsResp.json();
            setStats(statsData.stats);
          }
        } else {
          console.error('Failed to update status');
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Modal handlers
  function handleCloseNotifyModal() {
    setShowNotifyModal(false);
    setResidentEmail('');
    setSelectedUnit(null);
    setCustomMessage('');
  }

  async function handleSendNotification(e) {
    e.preventDefault();
  
    const userStr = localStorage.getItem('managerUser') || "";
    let managerName = "";
    let managerEmail = "";
    
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        managerName = userObj.first_name || "";  
        managerEmail = userObj.email || "";
      } catch (err) {
        console.error("Error parsing user data:", err);
      }
    }

    const payload = {
      unit: selectedUnit.unit,
      buildingId: buildingId,
      buildingAddress: buildingName,
      managerName: managerName,
      managerEmail: managerEmail,
      residentEmail: residentEmail,
      residentNotice: customMessage
    };

    try {
      console.log("Sending notification payload:", payload);

      const response = await fetch(`${API_BASE_URL}/api/notify-resident`, {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
          handleCloseNotifyModal();

        // Show success alert
        setShowInviteSuccess(true);
        setTimeout(() => setShowInviteSuccess(false), 3000);
      } else {
          alert("Error sending notification: " + data.error);
      }
    } catch (err) {
      console.error("Notification sending error:", err);
      alert("An error occurred while sending notification.");
    }
  }

  const EmptyState = () => (
    <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="text-center p-5">
          <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
            <FaFan />
          </div>
          <Card.Title style={{ 
            fontSize: '2rem', 
            marginBottom: '1.5rem', 
            color: '#224982',
            fontWeight: '600'
          }}>
            Dryer Vent Maintenance
          </Card.Title>
          <Card.Text style={{ 
            color: '#596272', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Ensure the safety and efficiency of your building's dryer vents with Hudson's comprehensive management solution.
            Get regular inspections, professional cleaning services, and detailed maintenance tracking to prevent fire hazards
            and maintain optimal performance.
          </Card.Text>
          <div style={{ fontSize: '1.15rem', color: '#224982', fontWeight: 500 }}>
            Email <a href="mailto:service@hudsonhvac.ca">service@hudsonhvac.ca</a> for a free proposal
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const LoadingState = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <Spinner animation="border" role="status" style={{ color: '#224982' }}>
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div
          className="dashboard-sidebar-host"
          onMouseEnter={() => setCollapsed(false)}
          onMouseLeave={() => setCollapsed(true)}
          style={{ height: '100vh' }}
        >
          <Sidebar
            managerName={buildingManagerName}
            activeKey="dryerVents"
            onNavigate={handleNavigate}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
            buildingName={buildingName}
            buildingPhotoUrl={buildingPhotoUrl}
            showProjectsTab={hasProjectContract}
            showValveAuditTab={hasValveAuditContract}
          />
        </div>
        <div
          className="dashboard-content manager-dashboard"
          style={{
            flex: 1,
            minWidth: 0,
            marginLeft: collapsed ? 64 : 220,
            transition: 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'translate3d(0,0,0)',
            willChange: 'margin-left',
            backfaceVisibility: 'hidden',
          }}
        >
          <DashboardNavbar
            logoutInSidebar
            buildingName={buildingName}
            buildingId={buildingId}
            contractLabel="Dryer Vent Cleaning"
          />

          <Header
            maintenanceCycles={maintenanceCycles}
            selectedCycle={selectedCycle}
            setSelectedCycle={handleCycleChange}
            selectedMaintenance={selectedMaintenance}
            contractLabel="Dryer Vent Cleaning"
            buildingName={buildingName}
            reportFieldName={dataFormat === "new" ? "dryerReport" : "finalReport"} // New format uses dryerReport, old format uses finalReport
          />

          {isLoading ? (
            <LoadingState />
          ) : !hasDryerVentContract ? (
            <EmptyState />
          ) : (
            <>
              {showInviteSuccess && (
                <Alert
                  variant="success"
                  style={{
                    position: 'fixed',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    margin: 0,
                    padding: '0.5rem 1rem',
                  }}
                >
                  <strong>Notification sent successfully!</strong>
                </Alert>
              )}

              {/* Stats Pills — new PillsV2 layout (3 wide + No Access + Health) */}
              <Pills
                stats={pillsStats}
                hvacType="dryer-vent"
                unitsData={normalizedUnits}
                onShowMissed={() => setStatusFilter(['Missed'])}
              />

              {/* Search + Filters - using the same components */}
              <div
                className="manager-data-filters"
                style={{
                  width: '90%',
                  margin: '1rem auto',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'nowrap',
                }}
              >
                <div style={{ flex: 4 }}>
                  <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>
                <div style={{ flex: 2 }}>
                  <StatusMultiSelect
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                  />
                </div>
              </div>

              {/* Table - using the same FancoilTable component */}
              <FancoilTable
                hvacType="dryer-vent"
                pageData={pageData}
                sortedData={sortedData}
                startIndex={startIndex}
                endIndex={endIndex}
                currentPage={currentPage}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                handlePrevPage={handlePrevPage}
                handleNextPage={handleNextPage}
                handleRowsPerPageChange={handleRowsPerPageChange}
                showModal={showModal}
                carouselPhotos={carouselPhotos}
                carouselIndex={carouselIndex}
                handleOpenCarousel={handleOpenCarousel}
                handleCloseModal={handleCloseModal}
                handleSelectCarousel={handleSelectCarousel}
                isVideoUrl={isVideoUrl}
                buildingId={buildingId}
                onNotifyResident={onNotifyResident}         
                onUpdateStatus={onUpdateStatus}
                dataFormat={dataFormat}
              />
            </>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'center', color: '#888', fontSize: '0.95rem', margin: '1.5rem 0 0.5rem 0' }}>
        © 2025 Hudson Condominium Solutions Inc.
      </div>

      <NotifyResidentModal
        show={showNotifyModal}
        handleClose={handleCloseNotifyModal}
        handleSend={handleSendNotification}
        residentEmail={residentEmail}
        setResidentEmail={setResidentEmail}
        customMessage={customMessage}
        unitData={selectedUnit}
        buildingId={buildingId}
        buildingName={buildingName}
        setCustomMessage={setCustomMessage}
        hvacType="dryer-vent"
      />
    </>
  );
}

export default DryerVents;
